/**
 * TalentProfile — Employment & Staffing candidate intake.
 *
 * Reads and writes the participant's `participant_profiles` record via
 * `PATCH /api/participant/talent-profile`. When arrived with `?welcome=1`
 * (immediately after Candidate Network signup), shows the confirmation
 * banner. After the user submits the profile, they land in the existing
 * participant dashboard.
 */
import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { AppShell } from "../components/cod/Layout";
import { Card, CardContent } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import { CheckCircle2, Save, Send } from "lucide-react";
import client from "../lib/api";

const csv = (v) => (Array.isArray(v) ? v.join(", ") : (v || ""));
const fromCsv = (s) => s.split(",").map(x => x.trim()).filter(Boolean);

export default function TalentProfile() {
  const loc = useLocation();
  const navigate = useNavigate();
  const welcome = new URLSearchParams(loc.search).get("welcome") === "1";

  const [profile, setProfile] = useState(null);
  const [form, setForm] = useState({
    positions_of_interest: "", employment_goals: "", skills: "",
    work_history_text: "",   // free-text in the UI, mapped to structured on save
    available_start_date: "", preferred_locations: "",
    work_authorization: "", certifications: "",
  });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await client.get("/participants/me/profile");
        setProfile(data || {});
        if (data) {
          setForm({
            positions_of_interest: csv(data.positions_of_interest),
            employment_goals: data.employment_goals || "",
            skills: csv(data.skills),
            work_history_text: (data.work_history || [])
              .map(w => (typeof w === "string" ? w : `${w.title || ""}${w.employer ? " · " + w.employer : ""}${w.years ? " · " + w.years : ""}`))
              .join("\n"),
            available_start_date: data.available_start_date || "",
            preferred_locations: csv(data.preferred_locations),
            work_authorization: data.work_authorization || "",
            certifications: csv(data.certifications),
          });
        }
      } catch { /* leave form empty */ }
    })();
  }, []);

  const buildPayload = (mark_ready = false) => ({
    positions_of_interest: fromCsv(form.positions_of_interest),
    employment_goals: form.employment_goals || null,
    skills: fromCsv(form.skills),
    work_history: form.work_history_text
      .split("\n").map(l => l.trim()).filter(Boolean)
      .map(l => ({ title: l })),
    available_start_date: form.available_start_date || null,
    preferred_locations: fromCsv(form.preferred_locations),
    work_authorization: form.work_authorization || null,
    certifications: fromCsv(form.certifications),
    mark_ready,
  });

  const save = async (mark_ready) => {
    setBusy(true);
    try {
      await client.patch("/participant/talent-profile", buildPayload(mark_ready));
      if (mark_ready) {
        toast.success("Candidate Profile submitted — COD has been notified.");
        navigate("/app/home");
      } else {
        toast.success("Draft saved.");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setBusy(false);
    }
  };

  const isReady = profile?.talent_status === "ready_for_review";

  return (
    <AppShell>
      <div className="max-w-3xl mx-auto space-y-6">
        {welcome && (
          <Card className="border-2" style={{ borderColor: "var(--cod-red)" }} data-testid="talent-welcome-banner">
            <CardContent className="pt-6 flex gap-3 items-start">
              <CheckCircle2 className="shrink-0" size={22} color="var(--cod-red)"/>
              <div>
                <div className="font-serif text-lg text-slate-900 mb-1">You're now part of the Chicago Opportunity Desk Candidate Network.</div>
                <p className="text-sm text-slate-700">Complete your Candidate Profile so COD can review your skills and employment goals and consider you for matching opportunities.</p>
              </div>
            </CardContent>
          </Card>
        )}

        <div>
          <p className="cod-eyebrow">Candidate Network</p>
          <h1 className="font-serif text-3xl sm:text-4xl text-slate-900 mt-2">Candidate Profile</h1>
          {isReady && (
            <Badge className="mt-3 text-xs" style={{background:"#DCFCE7", color:"#166534"}} data-testid="talent-status-badge">
              Submitted — Ready for review by COD
            </Badge>
          )}
        </div>

        <Card>
          <CardContent className="pt-6 space-y-5">
            <div>
              <Label>Positions / Employment Goals</Label>
              <Input placeholder="Warehouse Associate, Delivery Driver, Home Health Aide…"
                     value={form.positions_of_interest}
                     onChange={e=>setForm({...form, positions_of_interest:e.target.value})}
                     data-testid="talent-positions" className="mt-1.5 h-11"/>
              <p className="text-xs text-slate-500 mt-1">Comma-separated. What roles are you looking for?</p>
            </div>

            <div>
              <Label>What kind of work are you hoping for?</Label>
              <Textarea rows={3} placeholder="Full-time hours, near West Loop, willing to work weekends…"
                        value={form.employment_goals}
                        onChange={e=>setForm({...form, employment_goals:e.target.value})}
                        data-testid="talent-goals" className="mt-1.5"/>
            </div>

            <div>
              <Label>Skills</Label>
              <Input placeholder="Forklift, customer service, spreadsheets, Spanish…"
                     value={form.skills}
                     onChange={e=>setForm({...form, skills:e.target.value})}
                     data-testid="talent-skills" className="mt-1.5 h-11"/>
              <p className="text-xs text-slate-500 mt-1">Comma-separated.</p>
            </div>

            <div>
              <Label>Work Experience</Label>
              <Textarea rows={4} placeholder={"One line per role, e.g.\nWarehouse Associate · Amazon · 2 years\nCashier · Jewel-Osco · 1 year"}
                        value={form.work_history_text}
                        onChange={e=>setForm({...form, work_history_text:e.target.value})}
                        data-testid="talent-history" className="mt-1.5"/>
            </div>

            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label>Available start date</Label>
                <Input type="date" value={form.available_start_date}
                       onChange={e=>setForm({...form, available_start_date:e.target.value})}
                       data-testid="talent-start-date" className="mt-1.5 h-11"/>
              </div>
              <div>
                <Label>Work authorization</Label>
                <Select value={form.work_authorization}
                        onValueChange={v=>setForm({...form, work_authorization:v})}>
                  <SelectTrigger className="mt-1.5 h-11" data-testid="talent-work-auth"><SelectValue placeholder="Choose…"/></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="us_citizen">U.S. Citizen</SelectItem>
                    <SelectItem value="permanent_resident">Permanent Resident</SelectItem>
                    <SelectItem value="work_visa">Work Visa</SelectItem>
                    <SelectItem value="other_authorized">Other Authorized</SelectItem>
                    <SelectItem value="prefer_not_to_say">Prefer not to say</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <Label>Preferred work locations</Label>
              <Input placeholder="Chicago, Cicero, Oak Park…"
                     value={form.preferred_locations}
                     onChange={e=>setForm({...form, preferred_locations:e.target.value})}
                     data-testid="talent-locations" className="mt-1.5 h-11"/>
              <p className="text-xs text-slate-500 mt-1">Comma-separated.</p>
            </div>

            <div>
              <Label>Certifications / licenses</Label>
              <Input placeholder="CDL-A, CPR/BLS, Food Handler, OSHA-10, CNA…"
                     value={form.certifications}
                     onChange={e=>setForm({...form, certifications:e.target.value})}
                     data-testid="talent-certs" className="mt-1.5 h-11"/>
              <p className="text-xs text-slate-500 mt-1">Comma-separated, if applicable.</p>
            </div>

            <div className="text-xs text-slate-500 pt-2 border-t border-slate-200">
              Resume upload uses the existing Documents area — upload your resume there with category "Resume" and COD staff will see it linked to your profile.
            </div>
          </CardContent>
        </Card>

        <div className="flex flex-wrap items-center justify-end gap-2">
          <Button variant="outline" disabled={busy} onClick={()=>save(false)} data-testid="talent-save-draft">
            <Save size={14} className="mr-1"/> Save draft
          </Button>
          <Button className="cod-bg-red" disabled={busy} onClick={()=>save(true)} data-testid="talent-submit">
            <Send size={14} className="mr-1"/> Submit for review
          </Button>
        </div>
      </div>
    </AppShell>
  );
}
